import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, TextArea } from '../styles/GlobalStyles';
import { useAuth } from '../contexts/AuthContext';
import { bugReportService } from '../services/bugReportService';
import { APP_VERSION, ENVIRONMENT } from '../version';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
`;

const ModalContent = styled.div`
  background: var(--card-background);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-md);
  transition: all 0.2s ease;

  &:hover {
    background: var(--error-color)15;
    color: var(--error-color);
  }
`;

const MetadataSection = styled.div`
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const MetadataTitle = styled.h3`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 var(--spacing-sm) 0;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--spacing-xs) var(--spacing-md);
  font-size: 0.875rem;
`;

const MetadataLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
`;

const MetadataValue = styled.span`
  color: var(--text-primary);
  font-family: 'Courier New', monospace;
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: var(--spacing-sm);
`;

const Required = styled.span`
  color: var(--error-color);
  margin-left: var(--spacing-xs);
`;

const CharCount = styled.span<{ $isNearLimit: boolean }>`
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: ${props => props.$isNearLimit ? 'var(--error-color)' : 'var(--text-secondary)'};
  margin-top: var(--spacing-xs);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-md);
  justify-content: flex-end;
  margin-top: var(--spacing-xl);
`;

const SuccessMessage = styled.div`
  background: var(--success-color)15;
  border: 1px solid var(--success-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  color: var(--success-color);
  text-align: center;
  margin-bottom: var(--spacing-lg);
`;

const ErrorMessage = styled.div`
  background: var(--error-color)15;
  border: 1px solid var(--error-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  color: var(--error-color);
  margin-bottom: var(--spacing-lg);
`;

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TITLE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 2000;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const HiddenFileInput = styled.input`
  display: none;
`;

const FileButton = styled.button`
  background: var(--background-color);
  border: 1px dashed var(--border-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  transition: all 0.2s ease;
  min-height: 44px;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: 0.8rem;
  color: var(--text-primary);
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  padding: 2px;
  font-size: 1rem;
  line-height: 1;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<{ file: string; fileName: string; mimeType: string } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setSubmitError('File size must not exceed 5MB');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSubmitError('Only image files are allowed (PNG, JPEG, GIF, WebP)');
      return;
    }

    setSubmitError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      setAttachment({ file: base64, fileName: file.name, mimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await bugReportService.submitBugReport({
        title: title.trim(),
        description: description.trim(),
        userId: user?.id || 'unknown',
        clubId: user?.profile.clubId || 'none',
        environment: ENVIRONMENT || 'unknown',
        version: APP_VERSION,
        userEmail: user?.email || 'unknown',
        ...(attachment && {
          file: attachment.file,
          fileName: attachment.fileName,
          mimeType: attachment.mimeType
        })
      });

      setSubmitSuccess(true);
      setTitle('');
      setDescription('');
      setAttachment(null);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 3000);
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setTitle('');
      setDescription('');
      setAttachment(null);
      setSubmitSuccess(false);
      setSubmitError(null);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>🐛 Report a Bug</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isSubmitting}>
            ×
          </CloseButton>
        </ModalHeader>

        {submitSuccess && (
          <SuccessMessage>
            ✅ Bug report submitted successfully! You'll receive a confirmation email shortly.
          </SuccessMessage>
        )}

        {submitError && (
          <ErrorMessage>
            ❌ {submitError}
          </ErrorMessage>
        )}

        <MetadataSection>
          <MetadataTitle>Report Metadata</MetadataTitle>
          <MetadataGrid>
            <MetadataLabel>User ID:</MetadataLabel>
            <MetadataValue>{user?.id || 'Not available'}</MetadataValue>
            
            <MetadataLabel>Email:</MetadataLabel>
            <MetadataValue>{user?.email || 'Not available'}</MetadataValue>
            
            <MetadataLabel>Club ID:</MetadataLabel>
            <MetadataValue>{user?.profile.clubId || 'None'}</MetadataValue>
            
            <MetadataLabel>Environment:</MetadataLabel>
            <MetadataValue>{ENVIRONMENT || 'Unknown'}</MetadataValue>
            
            <MetadataLabel>Version:</MetadataLabel>
            <MetadataValue>v{APP_VERSION}</MetadataValue>
          </MetadataGrid>
        </MetadataSection>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              Bug Title<Required>*</Required>
            </Label>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX_LENGTH))}
              placeholder="Brief description of the issue"
              disabled={isSubmitting || submitSuccess}
              maxLength={TITLE_MAX_LENGTH}
              required
            />
            <CharCount $isNearLimit={title.length > TITLE_MAX_LENGTH * 0.9}>
              {title.length}/{TITLE_MAX_LENGTH}
            </CharCount>
          </FormGroup>

          <FormGroup>
            <Label>
              Description<Required>*</Required>
            </Label>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
              placeholder="Please describe the bug in detail:&#10;- What were you trying to do?&#10;- What happened?&#10;- What did you expect to happen?&#10;- Steps to reproduce (if known)"
              disabled={isSubmitting || submitSuccess}
              rows={10}
              maxLength={DESCRIPTION_MAX_LENGTH}
              required
            />
            <CharCount $isNearLimit={description.length > DESCRIPTION_MAX_LENGTH * 0.9}>
              {description.length}/{DESCRIPTION_MAX_LENGTH}
            </CharCount>
          </FormGroup>

          <FormGroup>
            <Label>Add Attachment</Label>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              onChange={handleFileChange}
              disabled={isSubmitting || submitSuccess}
            />
            {attachment ? (
              <FilePreview>
                <span>📎 {attachment.fileName}</span>
                <RemoveFileButton
                  type="button"
                  onClick={() => {
                    setAttachment(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  disabled={isSubmitting || submitSuccess}
                  aria-label="Remove attachment"
                >
                  ×
                </RemoveFileButton>
              </FilePreview>
            ) : (
              <FileButton
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting || submitSuccess}
              >
                📎 Add Attachment
              </FileButton>
            )}
          </FormGroup>

          <ButtonGroup>
            <Button
              type="button"
              $variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting || submitSuccess}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || submitSuccess || !title.trim() || !description.trim()}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
            </Button>
          </ButtonGroup>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};
